import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface SlackStatusData {
  connected: boolean;
  slackWorkspaceId?: string;
  expiresAt?: string;
  isExpired?: boolean;
  createdAt?: string;
}

export interface SlackAuthorizationData {
  authUrl: string;
}

export interface SlackUser {
  id: string;
  name: string;
  email?: string;
  real_name?: string;
  profile_image?: string;
}

export class SlackService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/slack";
  }

  async getSlackStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: SlackStatusData; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<SlackStatusData>>(
        `${this.baseUrl}/status`,
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch Slack connection status.";
      throw new Error(errorMessage);
    }
  }

  async connectSlack(
    config?: AxiosRequestConfig
  ): Promise<{ data: SlackAuthorizationData; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<SlackAuthorizationData>
      >(`${this.baseUrl}/connect`, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to connect Slack.";
      throw new Error(errorMessage);
    }
  }

  async disconnectSlack(
    config?: AxiosRequestConfig
  ): Promise<{ data: null; message: string }> {
    try {
      const response = await httpService.post<ApiResponse<null>>(
        `${this.baseUrl}/disconnect`,
        {},
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to disconnect Slack.";
      throw new Error(errorMessage);
    }
  }

  async fetchSlackUsers(
    config?: AxiosRequestConfig
  ): Promise<{ data: { users: SlackUser[] }; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<{ users: SlackUser[] }>
      >(`${this.baseUrl}/users`, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch Slack users.";
      throw new Error(errorMessage);
    }
  }
}

export const slackService = new SlackService();
